import { type Permission } from '@ibe/constants';
import { Button, ErrorState, LoadingState } from '@ibe/ui';
import { ArrowLeft } from 'lucide-react';
import { type UseQueryResult } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../auth/permission-guard.js';
import { errorTextFor } from '../../lib/api/error-message.js';

/**
 * Every detail screen in the portal.
 *
 * The three states are branched in a fixed order — pending, then error, then
 * content — and that order is the whole point. A screen that checks the error
 * first renders "not found" for the duration of every load, and one that
 * checks data first renders an empty shell.
 *
 * **It takes a query result rather than fetching.** The module already owns a
 * `useProperty`-style hook that encapsulates the key, the path and the schema,
 * and other screens need that hook too. Passing the config in here as well
 * would be a second copy of it. `ResourceListPage` is the deliberate exception:
 * pagination, sort, search and column selection all drive its query, so the
 * machinery has to own it.
 */

export interface ResourceDetailPageProps<TData> {
  readonly query: UseQueryResult<TData>;
  readonly permission: Permission;
  /** The breadcrumb target. A detail screen always has somewhere to go back to. */
  readonly parent: { readonly to: string; readonly label: string };
  readonly title: (data: TData) => string;
  readonly children: (data: TData) => ReactNode;
  /** Header actions — Edit, Cancel booking. */
  readonly actions?: ReactNode;
}

export function ResourceDetailPage<TData>({
  query,
  permission,
  parent,
  title,
  children,
  actions,
}: ResourceDetailPageProps<TData>): React.JSX.Element {
  const { search } = useLocation();
  const q = parent.to.indexOf('?');
  const parentTarget =
    q === -1
      ? { pathname: parent.to, search }
      : { pathname: parent.to.slice(0, q), search: parent.to.slice(q) };

  return (
    <PermissionGuard permission={permission} fallback={<NotPermitted />}>
      <section className="space-y-section">
        <nav aria-label="Breadcrumb">
          <Link className="text-caption underline-offset-4 hover:underline" to={parentTarget}>
            {parent.label}
          </Link>
        </nav>

        {query.isPending ? (
          <LoadingState label={`Loading ${parent.label.toLowerCase()}`} />
        ) : query.error !== null ? (
          <ErrorState
            description={errorTextFor(query.error) ?? ''}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : (
          <>
            <header className="flex flex-wrap items-start justify-between gap-3">
              <h1 className="text-page-title font-semibold">{title(query.data)}</h1>
              <Button asChild variant="outline">
                <Link to={parentTarget}>
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Back
                </Link>
              </Button>
              {actions}
            </header>
            {children(query.data)}
          </>
        )}
      </section>
    </PermissionGuard>
  );
}
